"use client";

import { FC, useEffect, useState } from "react";
import BFLModal from "@repo/shared-ui/BFLModal";
import { handleSSORedirect } from "./DematAccount";
import { handleClickDL } from "./DematAccountFunction";
import { trackDrawerContentDrawerClick } from "./unifiedCt";
import { useAppSelector, selectLoginStatus } from "@repo/web-store";
import { ICompany } from "../News/components/CompanyTag";
import { getListingCache } from "../../services/TradingMallService";

interface CompanyBuySellModalProps {
    isOpen: boolean;
    onClose: () => void;
    companies: ICompany[];
    headerName?: string;
    buyText?: string;
    sellText?: string;
    title?: string;
    enableScroll?: boolean;
}

interface CompanyWithTokens extends Omit<ICompany, 'nseToken' | 'bseToken' | 'exchId'> {
    nseToken?: number;
    bseToken?: number;
    exchId?: string;
    exchange?: number;
}

const CompanyBuySellModal: FC<CompanyBuySellModalProps> = ({
    isOpen,
    onClose,
    companies,
    headerName = "Bajaj Broking",
    buyText = "BUY",
    sellText = "SELL",
    title = "Select stock",
    enableScroll = true,
}) => {
    const loginStatus = useAppSelector(selectLoginStatus);
    const [companiesWithTokens, setCompaniesWithTokens] = useState<CompanyWithTokens[]>(companies);

    useEffect(() => {
        const fetchTokens = async () => {
            const companiesNeedingTokens = companies.filter(
                (c) => !(c as CompanyWithTokens).exchange && !c.token
            );

            if (companiesNeedingTokens.length === 0) {
                setCompaniesWithTokens(companies as CompanyWithTokens[]);
                return;
            }

            try {
                const listingsMap = await getListingCache();
                const updatedCompanies = companies.map((company) => {
                    const companyWithTokens = company as CompanyWithTokens;
                    if (companyWithTokens.exchange !== undefined && company.token !== undefined) {
                        return companyWithTokens;
                    }

                    const listing = listingsMap.get(company.symbol.toLowerCase());
                    if (!listing) {
                        return {
                            ...company,
                            exchange: 0,
                            token: 0,
                            nseToken: 0,
                            bseToken: 0,
                            exchId: "0",
                        };
                    }

                    const { nseToken = 0, bseToken = 0 } = listing;
                    const exchId = nseToken === 0 && bseToken !== 0 ? "2" : "0";

                    return {
                        ...company,
                        exchange: exchId === "2" ? 2 : 0,
                        token: exchId === "2" ? bseToken : nseToken,
                        nseToken,
                        bseToken,
                        exchId,
                    };
                });

                setCompaniesWithTokens(updatedCompanies);
            } catch (error) {
                console.error("Error fetching listing cache:", error);
                setCompaniesWithTokens(companies);
            }
        };

        if (isOpen && companies.length > 0) {
            fetchTokens();
        }
    }, [isOpen, companies]);

    const companiesCount = companies.length;

    const shouldTrackDrawerEvents = companiesCount > 1 && companiesCount < 4;

    const trackDrawerEvent = async (ctaText: string) => {
        if (!shouldTrackDrawerEvents) return;
        await trackDrawerContentDrawerClick(title, ctaText);
    };

    const handleBuyClick = async (company: CompanyWithTokens) => {
        await trackDrawerEvent(buyText);
        handleClickDL("BUY", company.symbol);

        const exchId = company.exchId ?? company.exchange?.toString() ?? "0";
        const nseToken = company.nseToken ?? (company.exchange === 0 ? (company.token ?? 0) : 0);
        const bseToken = company.bseToken ?? (company.exchange === 2 ? (company.token ?? 0) : 0);

        await handleSSORedirect(
            "buy",
            loginStatus,
            exchId,
            company.symbol,
            nseToken,
            bseToken,
            headerName,
        );
        onClose();
    };

    const handleSellClick = async (company: CompanyWithTokens) => {
        await trackDrawerEvent(sellText);
        handleClickDL("SELL", company.symbol);

        const exchId = company.exchId ?? company.exchange?.toString() ?? "0";
        const nseToken = company.nseToken ?? (company.exchange === 0 ? (company.token ?? 0) : 0);
        const bseToken = company.bseToken ?? (company.exchange === 2 ? (company.token ?? 0) : 0);

        await handleSSORedirect(
            "sell",
            loginStatus,
            exchId,
            company.symbol,
            nseToken,
            bseToken,
            headerName,
        );
        onClose();
    };

    return (
        <BFLModal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            enableScroll={enableScroll}
            bodyClassName="px-4 py-3"
            contentClassName="px-0"
            closeButtonClassName="text-link-primary absolute right-0 top-[12px] cursor-pointer text-xs min-w-[395px]  flex items-center justify-end"
            closeIconClassName="w-[14px] h-[14px] text-[14px] block"
            headerClassName="px-0 pb-5 pt-[12px] relative flex flex-col justify-center items-center rounded-lg bg-white w-full md:max-w-[424px]  lg:max-w-[424px]"
            maxWidthClassName="lg:max-w-[424px] md:max-w-[424px] flex flex-col justify-center "
            footerClassName="hidden"
            contentWrapperClassName="px-0"
        >
            <div className=" flex flex-col gap-[12px]">
                {companiesWithTokens.map((company, index) => (
                    <div
                        key={`${company.symbol}-${index}`}
                        className="flex items-center justify-between p-3 border border-[#E5E5E5] max-w-[392px] max-h-[54px]"
                    >
                        <div className="flex-1">
                            <p className="font-rubik leading-5 text-[#1A1A1A] sm:text-[16px] text-[14px] font-[400px]">
                                {company.name ?? company.symbol}
                            </p>
                        </div>
                        <div className="flex items-center gap-[10px]">
                            <button
                                className="cursor-pointer rounded-full bg-[#00A989] px-[21.5px] py-[6px] w-[72px] h-[30px] font-[500px] text-white hover:bg-[#00997a] transition-colors flex justify-center items-center text-[14px]"
                                onClick={() => handleBuyClick(company)}
                                data-testid={`buy-button-${company.symbol}`}
                            >
                                {buyText}
                            </button>
                            <button
                                className="cursor-pointer rounded-full bg-[#E85B0F] px-[21.5px] py-[6px] flex text-sm w-[72px] h-[30px] font-[500px] text-white hover:bg-[#d14a0d] transition-colors justify-center items-center text-[14px]"
                                onClick={() => handleSellClick(company)}
                                data-testid={`sell-button-${company.symbol}`}
                            >
                                {sellText}
                            </button>

                        </div>
                    </div>
                ))}
            </div>
        </BFLModal>
    );
};

export default CompanyBuySellModal;

